// Per-user storage: routes all localStorage.* calls through the signed-in user namespace.
import { scopedStorage as localStorage } from "./userStorage";
import { getPrayerPlan } from '../data/prayerPlans';
import { emitCrmEvent } from './crmEvents';

const STORAGE_KEY = 'prayer_calendar_events_v2';
const LEGACY_STORAGE_KEY = 'prayerTopics';
const LEGACY_DEMO_TOPICS = {
  '3-5-2023': 'Family',
  '7-5-2023': 'Nations',
  '12-5-2023': 'Church',
  '18-5-2023': 'Friends',
  '22-5-2023': 'Health',
  '25-5-2023': 'Guidance'
};

export const toDateKey = (date) => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, '0'),
  String(date.getDate()).padStart(2, '0')
].join('-');

export const fromDateKey = (key) => {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day, 12);
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const migrateLegacyTopics = () => {
  try {
    const legacy = JSON.parse(localStorage.getItem(LEGACY_STORAGE_KEY) || '{}');
    return Object.entries(legacy).flatMap(([legacyKey, value], index) => {
      if (LEGACY_DEMO_TOPICS[legacyKey] === value) return [];
      const [day, month, year] = legacyKey.split('-').map(Number);
      if (!day || !month || !year || !value) return [];
      const date = toDateKey(new Date(year, month - 1, day, 12));
      return [{
        id: `legacy-${date}-${index}`,
        date,
        title: typeof value === 'string' ? value : value.title,
        description: '',
        scripture: '',
        source: 'personal'
      }];
    });
  } catch {
    return [];
  }
};

export const getCalendarEvents = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
    const migrated = migrateLegacyTopics();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
    return migrated;
  } catch {
    return [];
  }
};

export const saveCalendarEvents = (events) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
  return events;
};

export const addCalendarEvent = (event) => {
  const events = getCalendarEvents();
  const nextEvent = {
    id: event.id || `personal-${Date.now()}`,
    date: event.date,
    title: event.title.trim(),
    description: event.description?.trim() || '',
    scripture: event.scripture?.trim() || '',
    source: event.source || 'personal',
    planId: event.planId || null
  };
  return saveCalendarEvents([nextEvent, ...events]);
};

export const removeCalendarEvent = (eventId) =>
  saveCalendarEvents(getCalendarEvents().filter((event) => event.id !== eventId));

export const schedulePrayerPlan = (planId, startDate = new Date()) => {
  const plan = getPrayerPlan(planId);
  if (!plan) throw new Error('Unknown prayer plan');
  const existing = getCalendarEvents().filter((event) => event.planId !== planId);
  const plannedEvents = plan.days.map((day, index) => {
    const date = toDateKey(addDays(startDate, index));
    return {
      id: `${plan.id}-${date}-${index + 1}`,
      date,
      title: day.title,
      description: day.description,
      scripture: day.scripture,
      source: 'plan',
      planId: plan.id,
      day: index + 1,
      duration: plan.duration
    };
  });
  emitCrmEvent('engagement', { kind: 'plan_started', planId: plan.id, duration: plan.duration });
  return saveCalendarEvents([...plannedEvents, ...existing]);
};

export const removePrayerPlan = (planId) =>
  saveCalendarEvents(getCalendarEvents().filter((event) => event.planId !== planId));

const escapeIcs = (value = '') => String(value)
  .replace(/\\/g, '\\\\')
  .replace(/\n/g, '\\n')
  .replace(/,/g, '\\,')
  .replace(/;/g, '\\;');

const compactDate = (key) => key.replace(/-/g, '');
const compactDateTime = (value) => new Date(value).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');

export const createIcsCalendar = (events) => {
  const rows = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Prayer Guide//Prayer Calendar//EN', 'CALSCALE:GREGORIAN'];
  events.forEach((event) => {
    const nextDate = toDateKey(addDays(fromDateKey(event.date), 1));
    const details = [event.description, event.scripture ? `Scripture: ${event.scripture}` : '', 'Created by Prayer Guide']
      .filter(Boolean)
      .join('\n\n');
    rows.push(
      'BEGIN:VEVENT',
      `UID:${escapeIcs(event.id)}@prayer-guide`,
      event.start ? `DTSTART:${compactDateTime(event.start)}` : `DTSTART;VALUE=DATE:${compactDate(event.date)}`,
      event.end ? `DTEND:${compactDateTime(event.end)}` : `DTEND;VALUE=DATE:${compactDate(nextDate)}`,
      `SUMMARY:${escapeIcs(`Prayer: ${event.title}`)}`,
      `DESCRIPTION:${escapeIcs(details)}`,
      'END:VEVENT'
    );
  });
  rows.push('END:VCALENDAR');
  return rows.join('\r\n');
};

export const downloadIcsCalendar = (events, filename = 'prayer-guide-calendar.ics') => {
  const blob = new Blob([createIcsCalendar(events)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

export const getGoogleCalendarUrl = (event) => {
  const nextDate = toDateKey(addDays(fromDateKey(event.date), 1));
  const details = [event.description, event.scripture ? `Scripture: ${event.scripture}` : '', 'Created by Prayer Guide']
    .filter(Boolean)
    .join('\n\n');
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `Prayer: ${event.title}`,
    dates: event.start && event.end
      ? `${compactDateTime(event.start)}/${compactDateTime(event.end)}`
      : `${compactDate(event.date)}/${compactDate(nextDate)}`,
    details
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const prayerCalendarService = {
  getCalendarEvents,
  addCalendarEvent,
  removeCalendarEvent,
  schedulePrayerPlan,
  removePrayerPlan,
  createIcsCalendar,
  downloadIcsCalendar,
  getGoogleCalendarUrl
};

export default prayerCalendarService;
